import {
	ButtonView,
	View,
	LabeledFieldView,
	createLabeledInputText,
	submitHandler,
	createDropdown,
	addListToDropdown,
	FormHeaderView,
	LabelView,
} from 'ckeditor5/src/ui';
import { icons } from 'ckeditor5/src/core';
import {
	FocusTracker,
	KeystrokeHandler,
	Collection,
	type Locale,
} from 'ckeditor5/src/utils';

export default class SmartButtonDialogView extends View {
	/**
	 * The text input view.
	 */
	public textInputView: LabeledFieldView;

	/**
	 * The URL input view.
	 */
	public urlInputView: LabeledFieldView;

	/**
	 * The target dropdown view.
	 */
	public targetDropdownView: any;

	/**
	 * The alignment radio view.
	 */
	public alignmentRadioView: any;

	/**
	 * The save button view.
	 */
	public saveButtonView: ButtonView;

	/**
	 * The cancel button view.
	 */
	public cancelButtonView: ButtonView;

	/**
	 * Focus tracker.
	 */
	public readonly focusTracker: FocusTracker;

	/**
	 * Keystroke handler.
	 */
	public readonly keystrokes: KeystrokeHandler;

	/**
	 * Observable property for button text.
	 */
	public declare text: string;

	/**
	 * Observable property for button URL.
	 */
	public declare url: string;

	/**
	 * Observable property for button target.
	 */
	public declare target: string;

	/**
	 * Observable property for button alignment.
	 */
	public declare alignment: string;

	/**
	 * @inheritDoc
	 */
	constructor(locale: Locale) {
		super(locale);

		this.set('text', '');
		this.set('url', '');
		this.set('target', '_blank');
		this.set('alignment', 'none');

		this.focusTracker = new FocusTracker();
		this.keystrokes = new KeystrokeHandler();

		// Form header
		const formHeaderView = new FormHeaderView(locale);
		formHeaderView.set({
			label: 'Custom Button',
		});

		// UI elements
		this.textInputView = this._createTextInput();
		this.urlInputView = this._createUrlInput();
		this.targetDropdownView = this._createTargetDropdown();
		this.alignmentRadioView = this._createAlignmentRadioGroup();
		this.saveButtonView = this._createButton(
			'OK',
			icons.check,
			'ck-button-save',
			true
		);
		this.cancelButtonView = this._createButton(
			'Cancel',
			icons.cancel,
			'ck-button-cancel'
		);

		this.setTemplate({
			tag: 'form',
			attributes: {
				class: ['ck', 'ck-smart-button-form'],
				tabindex: '-1',
			},
			children: [
				formHeaderView,
				{
					tag: 'div',
					attributes: {
						class: ['ck', 'ck-smart-button-form-content'],
					},
					children: [
						this.textInputView,
						this.urlInputView,
						this.targetDropdownView,
						this.alignmentRadioView,
					],
				},
				{
					tag: 'div',
					attributes: {
						class: ['ck', 'ck-smart-button-form-footer'],
					},
					children: [this.cancelButtonView, this.saveButtonView],
				},
			],
		});
	}

	/**
	 * @inheritDoc
	 */
	override render(): void {
		super.render();

		// Form validation
		submitHandler({
			view: this,
		});

		// Initialize focus tracking
		const focusableElements = [
			this.textInputView,
			this.urlInputView,
			this.targetDropdownView,
			this.saveButtonView,
			this.cancelButtonView,
		];

		// Register all focusable elements
		focusableElements.forEach((element) => {
			this.focusTracker.add(element.element!);
		});

		// Start listening for keystrokes
		this.keystrokes.listenTo(this.element!);
	}

	/**
	 * Focuses the first form field.
	 */
	focus(): void {
		this.textInputView.focus();
	}

	/**
	 * Creates the text input field.
	 */
	private _createTextInput(): LabeledFieldView {
		const locale = this.locale!;
		const t = locale.t;
		const inputView = new LabeledFieldView(locale, createLabeledInputText);

		inputView.label = t('Text');
		inputView.fieldView.placeholder = t('Button Text');

		inputView.fieldView.bind('value').to(this, 'text');
		inputView.fieldView.on('input', () => {
			this.text = inputView.fieldView.element!.value;
		});

		return inputView;
	}

	/**
	 * Creates the URL input field.
	 */
	private _createUrlInput(): LabeledFieldView {
		const locale = this.locale!;
		const t = locale.t;
		const inputView = new LabeledFieldView(locale, createLabeledInputText);

		inputView.label = t('URL');
		inputView.fieldView.placeholder = t('https://example.com');

		inputView.fieldView.bind('value').to(this, 'url');
		inputView.fieldView.on('input', () => {
			this.url = inputView.fieldView.element!.value;
		});

		return inputView;
	}

	/**
	 * Creates the target selection using radio buttons.
	 */
	private _createTargetDropdown(): View {
		const locale = this.locale!;
		const t = locale.t;
		const labelView = new LabelView(locale);
		labelView.text = t('Target');

		// Create container view for the radio buttons
		const containerView = new View(locale);

		// Create radio buttons for target options
		const selfRadioView = this._createTargetRadioButton('_self', '_self');
		const blankRadioView = this._createTargetRadioButton(
			'_blank',
			'_blank'
		);

		// Default value now set to _blank
		if (this.target === '_self') {
			// Do nothing, keep target as _self if already set
		} else {
			// Otherwise set to _blank as the default
			this.target = '_blank';
		}

		// Set initial selection
		this._setTargetButtonState(this.target, [
			selfRadioView,
			blankRadioView,
		]);

		// Handle changes in target
		this.on('change:target', () => {
			this._setTargetButtonState(this.target, [
				selfRadioView,
				blankRadioView,
			]);
		});

		containerView.setTemplate({
			tag: 'div',
			attributes: {
				class: ['ck', 'ck-smart-button-target-group'],
			},
			children: [
				labelView,
				{
					tag: 'div',
					attributes: {
						class: ['ck', 'ck-smart-button-target-options'],
					},
					children: [blankRadioView, selfRadioView], // _blank first to visually emphasize it
				},
			],
		});

		return containerView;
	}

	/**
	 * Creates a radio button for the target group.
	 */
	private _createTargetRadioButton(
		label: string,
		targetValue: string
	): ButtonView {
		const locale = this.locale!;
		const t = locale.t;
		const button = new ButtonView(locale);

		// Store the target value as a property on the button instance
		const buttonInstance = button as any;
		buttonInstance.targetValue = targetValue;

		button.set({
			label: t(label),
			class: [
				'ck-radio-button',
				`ck-target-${targetValue.replace('_', '')}`,
			],
			withText: true,
		});

		button.on('execute', () => {
			this.target = targetValue;
		});

		return button;
	}

	/**
	 * Updates the state of target radio buttons based on selected value.
	 */
	private _setTargetButtonState(
		selectedValue: string,
		buttons: Array<ButtonView>
	): void {
		buttons.forEach((button) => {
			const buttonInstance = button as any;
			const targetValue = buttonInstance.targetValue;
			button.isOn = targetValue === selectedValue;
		});
	}

	/**
	 * Gets the label for the target dropdown based on value.
	 */
	private _getTargetLabel(value: string): string {
		return value === '_blank'
			? 'New window (_blank)'
			: 'Same window (_self)';
	}

	/**
	 * Creates the alignment radio group.
	 */
	private _createAlignmentRadioGroup(): View {
		const locale = this.locale!;
		const t = locale.t;
		const labelView = new LabelView(locale);
		labelView.text = t('Alignment');

		// Create container view for the radio buttons
		const containerView = new View(locale);

		const noneRadioView = this._createRadioButton('None', 'none');
		const centerRadioView = this._createRadioButton('Center', 'center');
		const rightRadioView = this._createRadioButton('Right', 'right');
		const leftRadioView = this._createRadioButton('Left', 'left');

		// Set initial selection
		this._setRadioButtonState(this.alignment, [
			noneRadioView,
			centerRadioView,
			rightRadioView,
			leftRadioView,
		]);

		// Handle changes in alignment
		this.on('change:alignment', () => {
			this._setRadioButtonState(this.alignment, [
				noneRadioView,
				centerRadioView,
				rightRadioView,
				leftRadioView,
			]);
		});

		containerView.setTemplate({
			tag: 'div',
			attributes: {
				class: ['ck', 'ck-smart-button-alignment-group'],
			},
			children: [
				labelView,
				{
					tag: 'div',
					attributes: {
						class: ['ck', 'ck-smart-button-alignment-options'],
					},
					children: [
						noneRadioView,
						centerRadioView,
						rightRadioView,
						leftRadioView,
					],
				},
			],
		});

		return containerView;
	}

	/**
	 * Creates a radio button for the alignment group.
	 */
	private _createRadioButton(
		label: string,
		alignmentValue: string
	): ButtonView {
		const locale = this.locale!;
		const t = locale.t;
		const button = new ButtonView(locale);

		// Store the alignment value as a property on the button instance
		const buttonInstance = button as any;
		buttonInstance.alignmentValue = alignmentValue;

		button.set({
			label: t(label),
			class: ['ck-radio-button', `ck-alignment-${alignmentValue}`],
			withText: true,
		});

		button.on('execute', () => {
			this.alignment = alignmentValue;
		});

		return button;
	}

	/**
	 * Updates the state of radio buttons based on selected value.
	 */
	private _setRadioButtonState(
		selectedValue: string,
		buttons: Array<ButtonView>
	): void {
		buttons.forEach((button) => {
			const buttonInstance = button as any;
			const alignmentValue = buttonInstance.alignmentValue;
			button.isOn = alignmentValue === selectedValue;
		});
	}

	/**
	 * Creates a button.
	 */
	private _createButton(
		label: string,
		icon: string,
		className: string,
		isPrimary = false
	): ButtonView {
		const locale = this.locale!;
		const button = new ButtonView(locale);

		button.set({
			label,
			icon,
			tooltip: true,
			class: className,
		});

		if (isPrimary) {
			button.type = 'submit';
			button.isEnabled = true;
		} else {
			button.delegate('execute').to(this, 'cancel');
		}

		return button;
	}

	/**
	 * @inheritDoc
	 */
	override destroy(): void {
		super.destroy();
		this.focusTracker.destroy();
		this.keystrokes.destroy();
	}
}
